import { register } from 'mixpanel-browser';
import { useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  DateInput,
  Flex,
  Input,
  InputTextArea,
  Modal,
  RadioButton,
  Select,
  Text,
} from '@/lib/v2/components';
import { eventOnChangeDateInput } from '@/lib/v2/components/DateInput/DateInput';
import { Option } from '@/lib/v2/components/Select/Select';

import { ButtonActions } from './components/ButtonActions';
import {
  EStrategyOptionsName,
  ICreateEditCampaignsProps,
  IFormInput,
  MAX_MIN_LENGTH,
  STRATEGY_OPTIONS,
  TRANSLATE_MODAL,
  TRANSLATE_MODAL_INPUTS,
} from './constants';
const CreateEditCampaigns = ({ show, onClose }: ICreateEditCampaignsProps) => {
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<IFormInput>();
  const { t } = useTranslation();
  const [hasOtherStrategy, setHasOtherStrategy] = useState<boolean>(false);
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([null, null]);
  const [startDate, endDate] = dateRange;

  const VALIDATION_RULES = {
    name: {
      required: {
        value: true,
        message: t(`${TRANSLATE_MODAL}.INPUTS_ERROR.required`),
      },
      minLength: {
        value: MAX_MIN_LENGTH.MIN_LENGTH,
        message: t(`${TRANSLATE_MODAL}.INPUTS_ERROR.minLength`, {
          name: t(`${TRANSLATE_MODAL_INPUTS}.name.label`).toLowerCase(),
          min: MAX_MIN_LENGTH.MIN_LENGTH,
        }),
      },
      maxLength: {
        value: MAX_MIN_LENGTH.NAME_MAX,
        message: t(`${TRANSLATE_MODAL}.INPUTS_ERROR.maxLength`, {
          name: t(`${TRANSLATE_MODAL_INPUTS}.name.label`).toLowerCase(),
          max: MAX_MIN_LENGTH.NAME_MAX,
        }),
      },
    },
    type: {
      required: {
        value: true,
        message: t(`${TRANSLATE_MODAL}.INPUTS_ERROR.required`),
      },
    },
    objective: {
      required: {
        value: true,
        message: t(`${TRANSLATE_MODAL}.INPUTS_ERROR.required`),
      },
      minLength: {
        value: MAX_MIN_LENGTH.MIN_LENGTH,
        message: t(`${TRANSLATE_MODAL}.INPUTS_ERROR.minLength`, {
          name: t(`${TRANSLATE_MODAL_INPUTS}.objective.label`).toLowerCase(),
          min: MAX_MIN_LENGTH.MIN_LENGTH,
        }),
      },
      maxLength: {
        value: MAX_MIN_LENGTH.OBJECTIVE_MAX,
        message: t(`${TRANSLATE_MODAL}.INPUTS_ERROR.maxLength`, {
          name: t(`${TRANSLATE_MODAL_INPUTS}.objective.label`).toLowerCase(),
          max: MAX_MIN_LENGTH.OBJECTIVE_MAX,
        }),
      },
    },
    strategy: {
      required: {
        value: true,
        message: t(`${TRANSLATE_MODAL}.INPUTS_ERROR.required`),
      },
    },
    otherStrategy: {
      required: {
        value: true,
        message: t(`${TRANSLATE_MODAL}.INPUTS_ERROR.required`),
      },
      minLength: {
        value: MAX_MIN_LENGTH.MIN_LENGTH,
        message: t(`${TRANSLATE_MODAL}.INPUTS_ERROR.minLength`, {
          name: t(`${TRANSLATE_MODAL_INPUTS}.otherStrategy.label`).toLowerCase(),
          min: MAX_MIN_LENGTH.MIN_LENGTH,
        }),
      },
      maxLength: {
        value: MAX_MIN_LENGTH.OTHER_STRATEGY_MAX,
        message: t(`${TRANSLATE_MODAL}.INPUTS_ERROR.maxLength`, {
          name: t(`${TRANSLATE_MODAL_INPUTS}.otherStrategy.label`).toLowerCase(),
          max: MAX_MIN_LENGTH.OTHER_STRATEGY_MAX,
        }),
      },
    },
    date: {
      required: {
        value: true,
        message: t(`${TRANSLATE_MODAL}.INPUTS_ERROR.required`),
      },
    },
  };

  const handleChangeStrategy = useCallback((value: Option | Option[]) => {
    const data = value as Option;
    setHasOtherStrategy(data?.value === EStrategyOptionsName.OTHER.toLowerCase());
  }, []);

  const handleCloseModal = useCallback(() => onClose(), [onClose]);

  const handleOnChangeDate: eventOnChangeDateInput = useCallback((event) => {
    if (Array.isArray(event)) setDateRange(event as [Date | null, Date | null]);
  }, []);

  const handleSubmitData = useCallback((formData: IFormInput) => {
    console.log('🚀 ~ handleSubmitData ~ Save data:', formData);
    onClose();
  }, []);

  return (
    <Modal open={show} onClose={onClose}>
      <div className="flex flex-col gap-2 divide-y">
        <header>
          <Text as="h3" fontWeight="medium" variant="sub-headline">
            {t(`${TRANSLATE_MODAL}.titleModal`)}
          </Text>
        </header>
        <section className="flex w-full flex-col pt-6">
          <form
            autoComplete="off"
            id="createEditCampaigns"
            onSubmit={handleSubmit(handleSubmitData)}
          >
            <Flex column withGap alignment="spaceEvenly" gapSize="small">
              <Input
                isRequired
                control={control}
                id="name"
                label={t(`${TRANSLATE_MODAL_INPUTS}.name.label`)}
                name="name"
                placeHolder={t(`${TRANSLATE_MODAL_INPUTS}.name.placeholder`)}
                rules={VALIDATION_RULES.name}
                type="text"
              />
              <div className="w-full">
                <Text fontWeight="medium" variant="text">
                  {t(`${TRANSLATE_MODAL_INPUTS}.type.label`)}
                  {VALIDATION_RULES?.type?.required && <span className="text-red-500"> * </span>}
                </Text>
                <Flex withGap alignment="spaceBetween" gapSize="small">
                  <RadioButton
                    control={control}
                    hasError={!!errors?.type}
                    id="typeManual"
                    label={t(`${TRANSLATE_MODAL_INPUTS}.type.manual`)}
                    name="type"
                    rules={VALIDATION_RULES.type}
                    value="manual"
                    variant="button"
                  />
                  <RadioButton
                    control={control}
                    hasError={!!errors?.type}
                    id="typeAutomatic"
                    label={t(`${TRANSLATE_MODAL_INPUTS}.type.automatic`)}
                    name="type"
                    rules={VALIDATION_RULES.type}
                    value="automatic"
                    variant="button"
                  />
                </Flex>
                {errors && (
                  <Text color="alert" variant="text-sm">
                    {errors?.type?.message}
                  </Text>
                )}
              </div>
              <div className="w-full">
                <InputTextArea
                  isRequired
                  control={control}
                  id="objective"
                  label={t(`${TRANSLATE_MODAL_INPUTS}.objective.label`)}
                  name="objective"
                  placeHolder={t(`${TRANSLATE_MODAL_INPUTS}.objective.placeholder`)}
                  rows={3}
                  rules={VALIDATION_RULES.objective}
                />
              </div>
              <Select
                isRequired
                control={control}
                id="strategy"
                label={t(`${TRANSLATE_MODAL_INPUTS}.strategy.label`)}
                name="strategy"
                options={STRATEGY_OPTIONS}
                placeholder={t(`${TRANSLATE_MODAL_INPUTS}.strategy.placeholder`)}
                rules={VALIDATION_RULES.strategy}
                onChange={handleChangeStrategy}
              />
              {hasOtherStrategy && (
                <Input
                  isRequired
                  control={control}
                  id="otherStrategy"
                  label={t(`${TRANSLATE_MODAL_INPUTS}.otherStrategy.label`)}
                  name="otherStrategy"
                  placeHolder={t(`${TRANSLATE_MODAL_INPUTS}.otherStrategy.placeholder`)}
                  rules={VALIDATION_RULES.otherStrategy}
                  type="text"
                />
              )}
              <DateInput
                isRequired
                selectsRange
                control={control}
                endDate={endDate}
                id="date"
                label={t(`${TRANSLATE_MODAL_INPUTS}.date.placeholder`)}
                minDate={new Date()}
                name="date"
                rules={VALIDATION_RULES.date}
                startDate={startDate}
                onChange={handleOnChangeDate}
              />
            </Flex>
            <ButtonActions
              textCancelButton={t(`${TRANSLATE_MODAL}.cancelButton`)}
              textSubmitButton={t(`${TRANSLATE_MODAL}.successButton`)}
              onClose={handleCloseModal}
            />
          </form>
        </section>
      </div>
    </Modal>
  );
};
export default CreateEditCampaigns;
